import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Add, Delete, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import CtaUrlButtonForm from "../MessageInput/CtaUrlButtonForm";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  url: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-block",
  },
}));

const CtaButtonsManager = () => {
  const classes = useStyles();
  const [buttons, setButtons] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cta-url-buttons");
      setButtons(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = () => {
    setEditingButton(null);
    setFormOpen(true);
  };

  const handleEdit = button => {
    setEditingButton(button);
    setFormOpen(true);
  };

  const handleDelete = async button => {
    const confirmed = window.confirm(
      i18n.t("messagesInput.ctaUrl.deleteConfirm", {
        name: button.name || button.displayText,
      })
    );
    if (!confirmed) return;

    try {
      await api.delete(`/cta-url-buttons/${button.id}`);
      setButtons(prev => prev.filter(item => item.id !== button.id));
      toast.success(i18n.t("messagesInput.ctaUrl.deleted"));
      window.dispatchEvent(new Event("cta-buttons-changed"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaved = () => {
    load();
    window.dispatchEvent(new Event("cta-buttons-changed"));
  };

  return (
    <>
      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6">
          {i18n.t("settings.settings.ctaButtons.libraryTitle")}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {i18n.t("settings.settings.ctaButtons.libraryHelp")}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleCreate}
          style={{ marginBottom: 12 }}
        >
          {i18n.t("messagesInput.ctaUrl.createNew")}
        </Button>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("messagesInput.ctaUrl.name")}</TableCell>
              <TableCell>{i18n.t("messagesInput.ctaUrl.displayText")}</TableCell>
              <TableCell>{i18n.t("messagesInput.ctaUrl.url")}</TableCell>
              <TableCell align="right">
                {i18n.t("settings.settings.aiPrompts.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buttons.map(button => (
              <TableRow key={button.id}>
                <TableCell>{button.name}</TableCell>
                <TableCell>{button.displayText}</TableCell>
                <TableCell>
                  <span className={classes.url} title={button.url}>
                    {button.url}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <div className={classes.actions}>
                    <IconButton size="small" onClick={() => handleEdit(button)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(button)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!buttons.length ? (
              <TableRow>
                <TableCell colSpan={4}>
                  {i18n.t("messagesInput.ctaUrl.emptyList")}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <CtaUrlButtonForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingButton(null);
        }}
        initialButton={editingButton}
        onSaved={handleSaved}
      />
    </>
  );
};

export default CtaButtonsManager;
